import { toChecksumAddress } from 'web3-utils'
import ERC725, { ERC725JSONSchema } from '@erc725/erc725.js'
import LSP1Schema from '@erc725/erc725.js/schemas/LSP1UniversalReceiverDelegate.json'
import LSP3Schema from '@erc725/erc725.js/schemas/LSP3ProfileMetadata.json'
import LSP5Schema from '@erc725/erc725.js/schemas/LSP5ReceivedAssets.json'
import LSP6Schema from '@erc725/erc725.js/schemas/LSP6KeyManager.json'
import LSP10Schema from '@erc725/erc725.js/schemas/LSP10ReceivedVaults.json'
import LSP12Schema from '@erc725/erc725.js/schemas/LSP12IssuedAssets.json'
import LSP17Schema from '@erc725/erc725.js/schemas/LSP17ContractExtension.json'

import { EventHandlerMethod } from '@/shared/models/EventHandlerMethod'

/**
 * Get key string name based on the hash. Looks through ERC725YDataKeys.
 * ie. 0x4b80742de2bf82acb3630000 -> AddressPermissions:Permissions
 * ie. 0x5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5 -> LSP3Profile
 *
 * @param erc725Key hash of the erc725 key
 * @returns name of the key
 */
export const erc725NameFromKey = (
  erc725Key: string
): EventHandlerMethod | undefined => {
  // Search through all the known schemas
  const erc725js = new ERC725([
    ...LSP1Schema,
    ...LSP3Schema,
    ...LSP5Schema,
    ...LSP6Schema,
    ...LSP10Schema,
    ...LSP12Schema,
    ...LSP17Schema,
  ] as ERC725JSONSchema[])

  const schemaOfDataKey = erc725js.getSchema(erc725Key)

  // remove the dynamic key part from the data key
  // TODO: wait until a fix from the erc725.js library is available and remove this
  if (schemaOfDataKey?.keyType === 'MappingWithGrouping') {
    return schemaOfDataKey.name.replace(
      /:[\da-f]+$/i,
      ':<address>'
    ) as EventHandlerMethod
  }

  return schemaOfDataKey?.name as EventHandlerMethod
}

/**
 * Get first 0x + 24 chars of the erc725 key which represent key without dynamic address part
 * ie. 0x4b80742de2bf82acb3630000<address> -> 0x4b80742de2bf82acb3630000
 * @param erc725Key hash of the erc725 key
 * @returns key part from erc725 key
 */
export const getPermissionKey = (erc725Key: string): string =>
  erc725Key.slice(0, 26)

/**
 * Get dynamic address after key from the erc725 key
 * ie. 0x4b80742de2bf82acb3630000<address> -> <address>
 * @param erc725Key hash of the erc725 key
 * @returns address from the hash
 */
export const getAddressFromKey = (erc725Key: string): string => {
  const address = `0x` + erc725Key.slice(26)

  return toChecksumAddress(address)
}
